/**
 * Retail Cart Module
 * Handles retail-only cart experience: validation, personalization, customer form capture, and draft order submission.
 * 
 * Only active when window.retailContext.mode === 'retail'
 */

class RetailCart {
  constructor() {
    this.mode = window.retailContext?.mode || 'b2b'
    this.slug = window.retailContext?.slug || sessionStorage.getItem('retailSlug') || null
    this.apiBaseUrl = window.MAGNOLIA?.apiBaseUrl || 'https://magnolia-memorial-api.vercel.app'
    this.isActive = this.mode === 'retail'
    this._pollingActive = false
    this._processingServiceSkus = false
    this.init()
  }

  init() {
    this.observeModeChanges()

    if (!this.isActive) {
      return
    }

    // Disable SparkLayer's cart override in retail mode
    this.disableSparkLayerCartOverride()

    // Intercept SparkLayer cart-open events (e.g., view-update) before they fire
    this.interceptSparkLayerEvents()

    // Initialize retail cart event listeners
    this.setupAddToCartValidation()
    this.setupCustomerForm()
    this.setupCartPricingObserver()
    
    // Delay initial price application to allow DOM to fully load and cart count to render
    // This prevents the cart bubble from briefly showing correct count then resetting to 0
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Wait another frame to ensure all Shopify cart rendering is complete
        requestAnimationFrame(() => this.applyRetailCartPrices())
      })
    } else {
      // DOM already loaded, defer to next frame anyway
      requestAnimationFrame(() => this.applyRetailCartPrices())
    }
    
    document.addEventListener('cart:updated', () => this.applyRetailCartPrices())
  }

  /**
   * Disable SparkLayer's cart interception when in retail mode.
   * This allows our retail cart drawer to function independently.
   */
  disableSparkLayerCartOverride() {
    // Signal to SparkLayer (if loaded) that we're in retail mode and should skip cart override
    if (window.sparkOptions) {
      window.sparkOptions._retailModeActive = true
    }
    // Prevent SparkLayer from triggering cart redirects in retail mode
    if (window.Spark) {
      window.Spark._skipCartRedirect = true
    }

    // Intercept cart drawer opening to prevent SparkLayer redirect
    this.interceptCartDrawerOpening()
  }

  /**
   * Placeholder for potential future SparkLayer event interception.
   * Currently handled by global.js setupCartModeInterception for B2B users in retail mode.
   */
  interceptSparkLayerEvents() {
    // No-op: Cart icon interception is handled by global.js for B2B users
    // This method is kept for API compatibility and future use
  }

  /**
   * Intercept cart drawer/icon clicks to prevent SparkLayer from hijacking the cart.
   * In retail mode, we want to use our cart drawer instead of SparkLayer's.
   * Uses a continuous monitoring approach to catch SparkLayer handlers that attach later.
   */
  interceptCartDrawerOpening() {
    const self = this

    // Continuous monitoring to hide SparkLayer UI elements
    const hideSparkLayerUI = () => {
      if (window.retailContext?.mode !== 'retail') return

      // Find and hide all SparkLayer-specific elements
      const sparkElements = document.querySelectorAll(
        'spark-pdp, .spark-cart, .spark-modal, .spark-drawer, ' +
        '[data-spark]:not([data-spark="b2b-only"]), ' +
        '.spark-account, .spark-overlay'
      )

      sparkElements.forEach(el => {
        if (el.style.display !== 'none') {
          el.style.display = 'none'
        }
      })

      // Continue monitoring
      setTimeout(hideSparkLayerUI, 500)
    }

    // Start hiding SparkLayer UI
    hideSparkLayerUI()

    // Watch for programmatic cart openings via SparkLayer
    // SparkLayer might try to open its own cart drawer, so we need to prevent that
    const observer = new MutationObserver((mutations) => {
      if (window.retailContext?.mode !== 'retail') return

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Look for SparkLayer cart modals or overlays being added
              if (
                node.classList && (
                  node.classList.contains('spark-cart') ||
                  node.classList.contains('spark-modal') ||
                  node.classList.contains('spark-drawer') ||
                  node.getAttribute('data-spark') ||
                  node.tagName === 'SPARK-PDP'
                )
              ) {
                // Hide SparkLayer cart UI if it appears
                node.style.display = 'none'
              }
            }
          })
        }
      })
    })

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  /**
   * Setup cart observer to detect when products are added (via Customily or other means)
   * and automatically add service SKUs based on personalization.
   */
  setupAddToCartValidation() {
    // Open cart drawer when item is added to cart in retail mode
    if (typeof subscribe === 'function') {
      subscribe('cart-update', () => {
        if (window.retailContext?.mode === 'retail') {
          const drawer = document.querySelector('cart-drawer')
          if (drawer && typeof drawer.open === 'function') {
            drawer.open()
          }
        }
      })
    }
    
    this.setupServiceSkuAddition()
  }

  /**
   * Listen for cart updates and automatically add service SKUs when personalized items are detected.
   */
  setupServiceSkuAddition() {
    this._processedItemKeys = new Set()
    this._lastCartItemCount = 0
    
    if (typeof subscribe === 'function') {
      subscribe('cart-update', async (event) => {
        await new Promise(r => setTimeout(r, 200))
        await this.checkAndAddServiceSkus(event?.source)
      })
    }
  }

  /**
   * Check cart for personalized items and add necessary service SKUs.
   * Prevents duplicate processing and handles cart removals correctly.
   * @param {string} source - Event source ('product-form', 'cart-items', etc.)
   */
  async checkAndAddServiceSkus(source = 'unknown') {
    if (this._processingServiceSkus) return
    
    try {
      this._processingServiceSkus = true
      
      const response = await fetch('/cart.js')
      if (!response.ok) {
        console.warn('[RetailCart] cart fetch failed:', response.status)
        this._processingServiceSkus = false
        return
      }
      const cart = await response.json()
      
      if (!cart.items || cart.items.length === 0) {
        this._processingServiceSkus = false
        this._lastCartItemCount = 0
        this._processedItemKeys.clear()
        return
      }
      
      const mainProductCount = cart.items.filter(item => !['ENGTEXT', 'ENGART', 'CBENGRAVING', 'MVSTANDARD', 'MVCUSTOM', 'MVFULLFRAME'].includes(item.sku)).length
      
      const newItems = cart.items.filter(item => {
        const itemKey = this.getItemKey(item)
        if (['ENGTEXT', 'ENGART', 'CBENGRAVING', 'MVSTANDARD', 'MVCUSTOM', 'MVFULLFRAME'].includes(item.sku)) return false
        return !this._processedItemKeys.has(itemKey)
      })
      
      const itemsToProcess = newItems.slice(0, 1)
      
      // If source is 'cart-items', this is a removal/quantity change
      // Skip processing to prevent incrementing service SKUs
      if (source === 'cart-items') {
        const currentItemKeys = new Set()
        cart.items.forEach(item => {
          if (!['ENGTEXT', 'ENGART', 'CBENGRAVING', 'MVSTANDARD', 'MVCUSTOM', 'MVFULLFRAME'].includes(item.sku)) {
            currentItemKeys.add(this.getItemKey(item))
          }
        })
        
        for (const key of this._processedItemKeys) {
          if (!currentItemKeys.has(key)) {
            this._processedItemKeys.delete(key)
          }
        }
        
        this._lastCartItemCount = mainProductCount
        this._processingServiceSkus = false
        return
      }
      
      // No new items to process
      if (itemsToProcess.length === 0) {
        this._lastCartItemCount = mainProductCount
        this._processingServiceSkus = false
        return
      }
      
      this._lastCartItemCount = mainProductCount
      
      for (const item of itemsToProcess) {
        const itemKey = this.getItemKey(item)
        this._processedItemKeys.add(itemKey)
        
        const properties = item.properties || {}
        const hasCost = '_cost' in properties
        const hasVendor = '_vendor' in properties
        const vendor = item.vendor || properties._vendor
        
        if (!hasCost || !hasVendor) {
          try {
            const cost = item.price ? (item.price / 100).toFixed(2) : '0.00'
            const updates = {}
            if (!hasCost) updates._cost = cost
            if (!hasVendor) updates._vendor = vendor
            
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                properties: { ...properties, ...updates }
              })
            })
          } catch (error) {
            // Failed to update properties, continue without them
          }
        }
        
        // Teeinblue products store only opaque design IDs (`_customization_id` etc.) in cart
        // properties — no human-readable engraving selection.  Fall back to reading the live
        // DOM via validateCustomily(), the same function preCartUpdateListener uses in B2B mode.
        let lookupProps = properties
        if (
          source === 'product-form' &&
          '_customization_id' in properties &&
          typeof validateCustomily === 'function'
        ) {
          const validation = validateCustomily()
          if (validation.valid && Object.keys(validation.attributes).length > 0) {
            lookupProps = { ...properties, ...validation.attributes }
          }
        }

        const serviceSkus = this.getAdditionalSkusByVendor(vendor, lookupProps)
        
        if (serviceSkus.length > 0) {
          // Reuse the already-fetched `cart` for service SKU existence checks.
          // The _cost/_vendor write above only regenerates the main item's key —
          // service SKU items are untouched, so `cart.items` is still accurate here.
          for (const skuInfo of serviceSkus) {
            const existingItem = cart.items.find(cartItem => cartItem.sku === skuInfo.sku)
            
            if (existingItem) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: existingItem.key,
                  quantity: existingItem.quantity + skuInfo.qty
                })
              })
            } else {
              const variant = await this.findVariantBySkuFromStore(skuInfo.sku)
              if (variant) {
                const cartData = {
                  items: [{
                    id: variant.id,
                    quantity: skuInfo.qty,
                    properties: {
                      '_cost': this.normalizeVariantPriceToDollars(variant.price),
                      '_vendor': vendor
                    }
                  }]
                }
                await this.addToShopifyCart(cartData, true)
              }
            }
          }
        }

        // For Teeinblue items, write human-readable engraving attributes back to the cart item
        // so the cart drawer can display personalisation details and distinguish multiple items.
        // This runs regardless of whether service SKUs were added (covers "Add Engraving: No" too).
        //
        // Also writes _engraving_sku so getExpectedEngravingTotals() can reconcile quantities
        // on future qty changes without losing the DOM reference (page may have navigated away).
        //
        // IMPORTANT: Use a fresh cart fetch — any prior cart/change.js calls (e.g. _cost/_vendor)
        // regenerate the item key. The stale item.key from the top-of-function fetch would no-op.
        if ('_customization_id' in properties) {
          try {
            const freshCart = await fetch('/cart.js').then(r => r.json())
            // Match by exact _customization_id value — matching by SKU alone would pick the
            // wrong item when two personalised items of the same variant are in the cart.
            const freshItem = freshCart.items.find(i =>
              i.properties?.['_customization_id'] === properties['_customization_id']
            )
            if (freshItem) {
              // Build visible attrs with 'Add Engraving' pinned first, then remaining fields.
              // Only exclude underscore-prefixed internal keys.
              const addEngraving = lookupProps['Add Engraving']
              const visibleAttrs = {}
              if (addEngraving) visibleAttrs['Add Engraving'] = addEngraving
              for (const [k, v] of Object.entries(lookupProps)) {
                if (!k.startsWith('_') && k !== 'Add Engraving' && v) {
                  visibleAttrs[k] = v
                }
              }
              const engravingSkuProp = serviceSkus.length > 0 ? { _engraving_sku: serviceSkus[0].sku } : {}
              const resp = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: freshItem.key,
                  properties: { ...freshItem.properties, ...engravingSkuProp, ...visibleAttrs }
                })
              })
              if (!resp.ok) console.warn('[RetailCart] property write failed:', resp.status)
            }
          } catch (e) { console.warn('[RetailCart] property write error:', e) }
        }
      }
      
      this.refreshCartDrawer()
      this._processingServiceSkus = false
    } catch (error) {
      this._processingServiceSkus = false
    }
  }

  /**
   * Create a unique key for a cart item
   */
  getItemKey(item) {
    const personalizationId = item.properties?.['_customily-personalization-id']
      || item.properties?.['_customization_id']
      || ''
    return `${item.id}-${personalizationId}`
  }

  /**
   * Find a variant by SKU by fetching product data from Shopify.
   * Uses direct handle-based approach for service products instead of searching catalog.
   */
  async findVariantBySkuFromStore(sku) {
    try {
      // Strategy 1: Check pre-populated map (fastest)
      let variantId = window.skuVariantMap?.[sku]
      
      if (variantId) {
        return { id: variantId }
      }

      // Strategy 2: Direct product handle lookup (fast, direct)
      const handleMap = {
        'ENGTEXT': 'text-engraving',
        'ENGART': 'text-clipart-engraving',
        'CBENGRAVING': 'created-bracelets-engraving',
        // 'BELINTDISC': 'beloved-initial-additional-disc' (removed - product discontinued)
      }
      
      const handle = handleMap[sku]
      if (!handle) {
        return null
      }

      const response = await fetch(`/products/${handle}.js`)
      
      if (!response.ok) {
        return null
      }

      const product = await response.json()
      if (product.variants && product.variants.length > 0) {
        const variant = product.variants[0] // Use first variant
        
        // Cache it for future use
        window.skuVariantMap = window.skuVariantMap || {}
        window.skuVariantMap[sku] = variant.id
        
        return {
          id: variant.id,
          price: variant.price
        }
      }

      return null
    } catch (error) {
      return null
    }
  }

  /**
   * Normalize variant.price (products.json returns dollars as a string/number; fall back gracefully if cents)
   */
  normalizeVariantPriceToDollars(variantPrice) {
    const priceNum = Number(variantPrice)
    if (!Number.isFinite(priceNum)) return '0.00'

    if (priceNum >= 1000) {
      return (priceNum / 100).toFixed(2)
    }
    return priceNum.toFixed(2)
  }

  /**
   * Refresh cart drawer by fetching updated cart data and re-rendering.
   */
  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer')
    
    if (!cartDrawer) {
      return
    }
    
    const isOpen = cartDrawer.classList.contains('active')
    
    if (isOpen) {
      try {
        const sectionsParam = 'sections[]=cart-drawer'
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js?${sectionsParam}`)
        const data = await response.json()
        
        const drawerInner = cartDrawer.querySelector('.drawer__inner')
        if (drawerInner && data.sections['cart-drawer']) {
          const html = new DOMParser()
            .parseFromString(data.sections['cart-drawer'], 'text/html')
            .querySelector('.drawer__inner').innerHTML
          
          drawerInner.innerHTML = html
        }
        
        requestAnimationFrame(() => this.applyRetailCartPrices())
        
      } catch (error) {
        // Refresh failed, cart drawer remains as-is
      }
    }
  }

  /**
   * Convert validation attributes to Shopify cart properties.
   */
  attributesToProperties(attributes) {
    const properties = {}
    for (const [key, value] of Object.entries(attributes)) {
      properties[key] = value
    }
    return properties
  }

  /**
   * Determine which vendor-specific SKUs to add based on personalization selections.
   * Works with both validation attributes and cart item properties.
   * Mirrors SparkLayer preCartUpdateListener logic.
   */
  getAdditionalSkusByVendor(vendor, propertiesOrAttributes) {
    const additionalSkus = []
    
    // Normalize property names (Customily uses field names like "Custom Text Engraving", "Custom Discs", etc.)
    // Check multiple possible keys for each property:
    // - Validation attribute keys (from SparkLayer preCartUpdateListener): "Engraving", "Clipart", "Discs"
    // - Customily field names (from product form): "Custom Text Engraving", "Custom Discs"
    // - Lowercase variations: "engraving", "clipart", "discs"
    
    const engravingType = 
      propertiesOrAttributes['Add Engraving'] ||       // validateCustomily / SparkLayer output
      propertiesOrAttributes['Engraving'] || 
      propertiesOrAttributes['Custom Text Engraving'] ||
      propertiesOrAttributes['engraving']
    
    const hasClipart = 
      'Clipart' in propertiesOrAttributes || 
      'Custom Clipart' in propertiesOrAttributes ||
      'clipart' in propertiesOrAttributes
    
    const discsValue = 
      propertiesOrAttributes['Discs'] || 
      propertiesOrAttributes['Custom Discs'] ||
      propertiesOrAttributes['discs']

    if (vendor === 'Terrybear') {
      // Terrybear: add engraving SKU if engraving is selected
      if (engravingType === 'Yes' || engravingType === 'Text Only' || engravingType === 'Text + Clipart') {
        const sku = hasClipart || engravingType === 'Text + Clipart' ? 'ENGART' : 'ENGTEXT'
        additionalSkus.push({ sku, qty: 1 })
      }
    } else if (vendor === 'Created Bracelets') {
      // DORMANT (2026-08-28): Created Bracelets products are no longer carried, so this branch
      // is unreachable. Kept in case the line returns — do not delete. See VYT-409.
      // Created Bracelets: add engraving SKU if selected
      // engravingType can be: "Inside", "Outside", "Inside & Outside", "No", or other values
      if (engravingType && engravingType !== 'No') {
        additionalSkus.push({ sku: 'CBENGRAVING', qty: 1 })
      }

      // Created Bracelets: add additional discs if quantity specified
      // discsValue handling removed; BELINTDISC product no longer available
      // if (discsValue) {
      //   const discQty = parseInt(discsValue) - 1
      //   if (discQty > 0) {
      //     additionalSkus.push({ sku: 'BELINTDISC', qty: discQty })
      //   }
      // }
    } else if (vendor === 'MacKenzie Vault') {
      // MacKenzie Vault: add engraving SKU based on engraving type
      // engravingType can be: "Standard", "Custom", "Custom Full-Frame", or "No"
      if (engravingType && engravingType !== 'No') {
        let sku
        switch (engravingType) {
          case 'Standard':
            sku = 'MVSTANDARD'
            break
          case 'Custom':
            sku = 'MVCUSTOM'
            break
          case 'Custom Full-Frame':
            sku = 'MVFULLFRAME'
            break
          default:
            return additionalSkus
        }
        additionalSkus.push({ sku, qty: 1 })
      }
    }

    return additionalSkus
  }

  /**
   * Find a variant by SKU within product data.
   */
  findVariantBySku(productData, sku) {
    return productData.variants?.find((v) => v.sku === sku)
  }

  /**
   * Add items to Shopify cart via Cart API.
   * @param {Object} cartData - Cart data to add
   * @param {boolean} skipEvent - If true, don't dispatch cart:updated event (prevents infinite loops when adding service SKUs)
   */
  async addToShopifyCart(cartData, skipEvent = false) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData),
      })

      if (!response.ok) {
        throw new Error(`Cart add failed: ${response.statusText}`)
      }

      // Cart add successful; cart drawer should open automatically
      // Trigger custom event for cart updates UNLESS we're adding service SKUs
      // (to prevent infinite loop where service SKU addition re-triggers the listener)
      if (!skipEvent) {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { success: true },
          })
        )
      }
    } catch (error) {
      console.error('Failed to add to cart:', error)
      this.showError('Failed to add items to cart. Please try again.')
    }
  }

  /**
   * Display Customily validation errors to the user.
   */
  showCustomilyErrors(validationResult) {
    const errorEl = document.getElementById('customily-error')
    if (errorEl) {
      errorEl.innerHTML = validationResult.errors.map((err) => `<p>${err}</p>`).join('')
      errorEl.style.display = 'block'
    }

    // Add red borders to invalid elements
    validationResult.elementsWithErrors.forEach((el) => {
      el.style.border = '2px solid red'
    })
  }

  /**
   * Setup retail-only customer form in cart drawer.
   * Form captures end-customer info for draft order submission.
   */
  setupCustomerForm() {
    // Only set up once - use a flag to prevent duplicate event listeners
    if (this._customerFormSetup) {
      return
    }
    this._customerFormSetup = true
    
    // Use event delegation on the document to handle buttons that may not exist yet
    // This will work for both drawer and cart page buttons
    document.addEventListener('click', (e) => {
      if (e.target.id === 'retail-cart-review-btn' || e.target.id === 'cart-page-review-btn') {
        e.preventDefault()
        this.navigateToDraftOrderReview()
      }
    })
  }

  /**
   * Navigate to the draft order review page with cart data in sessionStorage.
   */
  async navigateToDraftOrderReview() {
    // Final reconciliation pass before committing cart data — catches any service SKUs that were
    // missed due to a transient fetch failure during the original add-to-cart flow.
    // Items already processed (key in _processedItemKeys) are skipped, so this is a no-op for
    // the happy path. Teeinblue items only benefit if the product page DOM is still present;
    // Customily items (with 'Add Engraving' in cart properties) are fully covered regardless.
    await this.checkAndAddServiceSkus('product-form')

    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()

      // Store cart data for draft order review page
      const cartData = {
        items: cart.items.map(item => ({
          id: item.id,
          title: item.title,
          sku: item.sku,
          quantity: item.quantity,
          price: item.price, // in cents
          attributes: item.properties || [],
        })),
        totalPrice: cart.total_price,
      }
      sessionStorage.setItem('draftOrderCartData', JSON.stringify(cartData))

      // Navigate to draft order review page
      window.location.href = '/pages/draft-order-review'
    } catch (error) {
      console.error('[Retail Cart] Error fetching cart:', error)
      alert('Unable to navigate to review page. Please try again.')
    }
  }

  /**
   * Show generic error message.
   */
  showError(message) {
    const cartDrawer = document.querySelector('cart-drawer')
    const errorEl = cartDrawer?.querySelector('#CartDrawer-CartErrors')
    if (errorEl) {
      errorEl.innerHTML = `<p class="error-message">${message}</p>`
      errorEl.scrollIntoView({ behavior: 'smooth' })
    }
  }

  /**
   * Observe cart drawer mutations to re-apply retail pricing when items change.
   */
  setupCartPricingObserver() {
    // Watch both cart drawer and main cart page
    const containers = [
      document.getElementById('CartDrawer-CartItems'),
      document.getElementById('main-cart-items')
    ].filter(Boolean)

    if (containers.length === 0) return

    const debouncedApply = (() => {
      let t
      return () => {
        clearTimeout(t)
        t = setTimeout(() => this.applyRetailCartPrices(), 50)
      }
    })()

    const mo = new MutationObserver(() => {
      if (this.mode === 'retail') {
        debouncedApply()
      }
    })

    containers.forEach((container) => {
      mo.observe(container, { childList: true, subtree: true })
    })
  }

  /**
   * Retrieve cached retail prices (dollars) for slug.
   */
  getCachedPrices(slug) {
    const cacheKey = `retail_branding_${slug}`
    const cached = localStorage.getItem(cacheKey)
    if (!cached) return {}
    try {
      const parsed = JSON.parse(cached)
      // Legacy cache entries written before Phase 1 have no cachedAt timestamp.
      // Evict them so they get replaced with a fresh, timestamped entry.
      if (!parsed.cachedAt) {
        localStorage.removeItem(cacheKey)
        return {}
      }
      return parsed.prices || {}
    } catch (e) {
      console.warn('Failed to parse cached prices', e)
      return {}
    }
  }

  /**
   * Persist merged prices (dollars) for slug back to cache.
   */
  savePricesToCache(slug, prices) {
    const cacheKey = `retail_branding_${slug}`
    const cached = localStorage.getItem(cacheKey)
    let branding = {}
    try {
      branding = cached ? JSON.parse(cached).branding || {} : {}
    } catch (e) {
      branding = {}
    }
    localStorage.setItem(cacheKey, JSON.stringify({ branding, prices, cachedAt: Date.now() }))
  }

  dollarsToCents(value) {
    const num = Number(value)
    if (!Number.isFinite(num)) return null
    return Math.round(num * 100)
  }

  /**
   * Ensure prices for given SKUs are cached; fetch missing from API (prices returned in dollars).
   */
  async ensurePricesForSkus(slug, skus) {
    const cached = this.getCachedPrices(slug)
    const missing = skus.filter((sku) => !cached[sku])
    
    if (missing.length === 0) {
      return cached
    }

    try {
      const resp = await fetch(`${this.apiBaseUrl}/api/pricing/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-shopify-customer-id': String(window.MAGNOLIA?.customerId || '') },
        body: JSON.stringify({ skus: missing })
      })
      
      if (resp.ok) {
        const payload = await resp.json()
        const fetchedPrices = payload.data || {}
        const merged = { ...cached, ...fetchedPrices }
        this.savePricesToCache(slug, merged)
        return merged
      }
    } catch (e) {
      // Silently fail - use cached prices
    }

    return cached
  }

  /**
   * Apply retail pricing to cart drawer items and totals; hides Shopify cost prices.
   */
  async applyRetailCartPrices() {
    if (this.mode !== 'retail' || !this.slug) {
      return
    }
    
    // Find cart items - look for .cart-item in both drawer and main cart page
    let cartContainer = document.getElementById('CartDrawer-CartItems')
    if (!cartContainer) {
      cartContainer = document.getElementById('main-cart-items')
    }
    if (!cartContainer) return
    
    const cartItems = Array.from(cartContainer.querySelectorAll('.cart-item'))
    
    if (cartItems.length === 0) return

    // Get SKUs from data attributes, or extract from item title if needed
    const skus = cartItems
      .map((item) => {
        return item.dataset.itemSku || item.querySelector('[data-item-sku]')?.dataset.itemSku
      })
      .filter(Boolean)

    let priceMap = this.getCachedPrices(this.slug)
    if (skus.some((sku) => !priceMap[sku])) {
      priceMap = await this.ensurePricesForSkus(this.slug, skus)
    }

    const toMoney = (sku) => {
      const dollars = priceMap[sku]
      const cents = this.dollarsToCents(dollars)
      if (!Number.isFinite(cents) || cents <= 0) return null
      return formatMoney(cents, window.Shopify.money_format)
    }

    // Update line prices
    cartItems.forEach((item) => {
      const sku = item.dataset.itemSku || item.querySelector('[data-item-sku]')?.dataset.itemSku
      if (!sku) {
        return
      }
      
      const formatted = toMoney(sku)
      
      // Handle main cart page structure (simple <span class="price">)
      const priceSpan = item.querySelector('td:nth-child(3) .price')
      if (priceSpan) {
        priceSpan.textContent = formatted || 'Could not load price'
        priceSpan.classList.add('retail-price')
      }
      
      // Handle total column (main cart page)
      const totalSpan = item.querySelector('td:nth-child(5) .price')
      if (totalSpan) {
        // For main cart, recalculate based on quantity
        const qtyInput = item.querySelector('.quantity__input')
        const qty = qtyInput ? Number(qtyInput.value || 1) : 1
        const totalPrice = formatted ? formatMoney(this.dollarsToCents(priceMap[sku]) * qty, window.Shopify.money_format) : 'Could not load price'
        totalSpan.textContent = totalPrice
        totalSpan.classList.add('retail-price')
      }
      
      // Also handle cart drawer structure if present
      const priceWrapper = item.querySelector('.cart-item__price-wrapper')
      if (priceWrapper) {
        const finalPrices = priceWrapper.querySelectorAll('.cart-item__final-price')
        const oldPrices = priceWrapper.querySelectorAll('.cart-item__old-price')
        
        finalPrices.forEach((node) => {
          node.textContent = formatted || 'Could not load price'
          node.classList.add('retail-price')
        })
        
        oldPrices.forEach((node) => {
          node.textContent = formatted || 'Could not load price'
          node.classList.add('retail-price')
        })
        
        const discountedWrapper = priceWrapper.querySelector('.cart-item__discounted-prices')
        if (discountedWrapper) {
          discountedWrapper.classList.add('retail-price')
        }
      }
    })

    // Update subtotal
    const subtotalEl = document.querySelector('.totals__subtotal-value')
    if (subtotalEl) {
      let subtotalCents = 0
      cartItems.forEach((item) => {
        const sku = item.dataset.itemSku || item.querySelector('[data-item-sku]')?.dataset.itemSku
        if (!sku) return
        const cents = this.dollarsToCents(priceMap[sku])
        const qtyInput = item.querySelector('.quantity__input')
        const qty = qtyInput ? Number(qtyInput.value || 0) : 0
        if (Number.isFinite(cents) && cents > 0 && Number.isFinite(qty)) {
          subtotalCents += cents * qty
        }
      })
      if (subtotalCents > 0) {
        subtotalEl.textContent = formatMoney(subtotalCents, window.Shopify.money_format)
        subtotalEl.classList.add('retail-price')
      } else {
        subtotalEl.textContent = 'Could not load price'
        subtotalEl.classList.add('retail-price')
      }
    }
  }

  /**
   * Observe mode changes and reinit if needed.
   */
  observeModeChanges() {
    const rootEl = document.documentElement
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'data-retail-mode') {
          const newMode = rootEl.getAttribute('data-retail-mode')
          if (newMode !== this.mode) {

            this.mode = newMode
            this.isActive = this.mode === 'retail'
            
            // When toggling to retail mode, ensure we have a valid slug
            // Priority: sessionStorage (canonical source) > window.retailContext.slug
            if (this.isActive) {
              // Try sessionStorage first (for both B2B and unauthenticated users)
              const storedSlug = sessionStorage.getItem('retailSlug')
              if (storedSlug) {
                this.slug = storedSlug
              } else if (window.retailContext?.slug) {
                // Fallback to window.retailContext (shouldn't normally be needed)
                this.slug = window.retailContext.slug
              }
            }
            
            // Re-init listeners if mode changes
            if (this.isActive) {
              this.setupAddToCartValidation()
              this.setupCustomerForm()
              // Re-apply prices now that slug is updated
              this.applyRetailCartPrices()
            }
          }
        }
      }
    })
    mo.observe(rootEl, { attributes: true })
  }
}

// Initialize retail cart on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.retailCartInstance) {
      window.retailCartInstance = new RetailCart()
    }
  })
} else {
  if (!window.retailCartInstance) {
    window.retailCartInstance = new RetailCart()
  }
}

// Failsafe: ensure instance is created even if timing issues occur
setTimeout(() => {
  if (!window.retailCartInstance && window.retailContext?.mode === 'retail') {
    window.retailCartInstance = new RetailCart()
  }
}, 500)

// Listen for cart updates from quick add and other sources
document.addEventListener('cart:updated', () => {
  if (window.retailCartInstance && window.retailCartInstance.isActive) {
    // Wait for cart items to be rendered before applying prices
    const waitForCartItems = (attempt = 1) => {
      const container = document.getElementById('CartDrawer-CartItems')
      const cartItems = container ? container.querySelectorAll('.cart-item') : []
      
      if (cartItems.length > 0) {
        window.retailCartInstance.applyRetailCartPrices()
      } else if (attempt < 10) {
        // Retry up to 10 times (1 second total)
        setTimeout(() => waitForCartItems(attempt + 1), 100)
      }
    }
    waitForCartItems()
  }
})

// Also listen for custom-element events from cart-drawer
document.addEventListener('cart-drawer:open', () => {
  if (window.retailCartInstance && window.retailCartInstance.isActive) {
    setTimeout(() => {
      window.retailCartInstance.applyRetailCartPrices()
    }, 150)
  }
})


